import { useState } from 'react';
import {
  App,
  Alert,
  Button,
  Form,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  Result,
  Table,
  Input,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import {
  bulkApi,
  type BulkOperationRequest,
  type BulkOperationResult,
  type BulkOperationType,
} from '@/api/bulkApi';
import { useProfiles } from '@/hooks/useProfiles';

const { Text, Paragraph } = Typography;

interface BulkOperationsModalProps {
  open: boolean;
  selectedDeviceIds: number[];
  selectedEnterpriseId?: number;   // Para filtrar profiles disponibles
  onClose: (changed: boolean) => void;
}

/**
 * Modal para ejecutar una operacion bulk sobre N devices seleccionados.
 *
 * Pasos:
 * 1. Usuario elige operacion (cambiar perfil / enviar comando / desactivar / reactivar)
 * 2. Segun la operacion, le pide parametros adicionales
 * 3. Pide confirmacion
 * 4. Ejecuta y muestra resultados con detalle por device
 */
export function BulkOperationsModal({
  open,
  selectedDeviceIds,
  selectedEnterpriseId,
  onClose,
}: BulkOperationsModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [operation, setOperation] = useState<BulkOperationType | undefined>();
  const [result, setResult] = useState<BulkOperationResult | null>(null);

  const { data: profiles } = useProfiles({
    pageSize: 100,
    enterpriseId: selectedEnterpriseId,
  });

  const mutation = useMutation({
    mutationFn: (req: BulkOperationRequest) => bulkApi.executeOnDevices(req),
    onSuccess: (data) => {
      setResult(data);
      const summary = `${data.succeeded} OK · ${data.failed} fallaron · ${data.skipped} omitidos`;
      if (data.failed === 0 && data.skipped === 0) {
        message.success('Operacion completada: ' + summary);
      } else {
        message.warning('Operacion completada con avisos: ' + summary);
      }
    },
    onError: (e: Error) => {
      message.error('Error: ' + e.message);
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const req: BulkOperationRequest = {
        deviceIds: selectedDeviceIds,
        operation: values.operation,
        parameter: values.parameter ?? null,
        commandParametersJson: values.commandParametersJson ?? null,
        continueOnError: true,
      };
      mutation.mutate(req);
    } catch {
      // Errores de validation: ya los muestra el form
    }
  };

  const handleClose = () => {
    const changed = result !== null && result.succeeded > 0;
    setResult(null);
    form.resetFields();
    setOperation(undefined);
    onClose(changed);
  };

  if (result) {
    // Mostrar resultados
    return (
      <Modal
        open={open}
        title="Resultado de la operacion"
        onCancel={handleClose}
        width={800}
        footer={[
          <Button key="close" type="primary" onClick={handleClose}>Cerrar</Button>,
        ]}
      >
        <Result
          status={result.failed === 0 && result.skipped === 0 ? 'success' : 'warning'}
          title={`${result.succeeded} de ${result.total} devices procesados con exito`}
          subTitle={
            <Space>
              <Tag color="success">{result.succeeded} OK</Tag>
              <Tag color="error">{result.failed} fallaron</Tag>
              <Tag>{result.skipped} omitidos</Tag>
            </Space>
          }
        />
        {(result.failed > 0 || result.skipped > 0) && (
          <Table
            size="small"
            rowKey="deviceId"
            dataSource={result.items.filter((i) => i.status !== 'Succeeded')}
            pagination={false}
            columns={[
              {
                title: 'Device',
                key: 'device',
                render: (_, item) => (
                  <Text>{item.deviceName ?? `#${item.deviceId}`}</Text>
                ),
              },
              {
                title: 'Estado',
                dataIndex: 'status',
                width: 120,
                render: (s) => (
                  <Tag color={s === 'Failed' ? 'error' : 'default'}>{s}</Tag>
                ),
              },
              {
                title: 'Mensaje',
                dataIndex: 'message',
                render: (m) => <Text type="secondary" style={{ fontSize: 12 }}>{m}</Text>,
              },
            ]}
          />
        )}
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Operacion masiva"
      onCancel={() => onClose(false)}
      okText="Ejecutar"
      cancelText="Cancelar"
      onOk={handleSubmit}
      confirmLoading={mutation.isPending}
      width={620}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message={`${selectedDeviceIds.length} dispositivos seleccionados`}
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="operation"
          label="Operacion"
          rules={[{ required: true, message: 'Elegi una operacion' }]}
        >
          <Select
            placeholder="Selecciona..."
            onChange={(v) => setOperation(v)}
            options={[
              { value: 'ChangeProfile', label: 'Cambiar de perfil' },
              { value: 'SendCommand', label: 'Enviar mismo comando' },
              { value: 'Deactivate', label: 'Desactivar (soft delete)' },
              { value: 'Reactivate', label: 'Reactivar' },
            ]}
          />
        </Form.Item>

        {operation === 'ChangeProfile' && (
          <Form.Item
            name="parameter"
            label="Profile destino"
            rules={[{ required: true, message: 'Elegi un perfil' }]}
            extra={selectedEnterpriseId
              ? 'Solo se muestran perfiles de la empresa seleccionada en el filtro.'
              : 'Mostrando todos los perfiles. Asegurate de que pertenezcan a la empresa de los devices.'}
          >
            <Select
              placeholder="Profile..."
              options={(profiles?.items ?? []).map((p) => ({
                value: String(p.id),
                label: `${p.displayNameEs} (${p.name})`,
              }))}
            />
          </Form.Item>
        )}

        {operation === 'SendCommand' && (
          <>
            <Form.Item
              name="parameter"
              label="Tipo de comando"
              rules={[{ required: true, message: 'Elegi un comando' }]}
            >
              <Select
                placeholder="Comando..."
                options={[
                  { value: 'Lock', label: 'Bloquear (LOCK)' },
                  { value: 'Reboot', label: 'Reiniciar (REBOOT)' },
                  { value: 'ResetPassword', label: 'Reset password (RESET_PASSWORD)' },
                  { value: 'StartLostMode', label: 'Modo perdido (START_LOST_MODE)' },
                  { value: 'StopLostMode', label: 'Salir modo perdido (STOP_LOST_MODE)' },
                  { value: 'Sync', label: 'Refrescar info (REQUEST_DEVICE_INFO)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="commandParametersJson"
              label="Parametros JSON (opcional)"
              tooltip='Ej: {"newPassword":"3465"} para RESET_PASSWORD'
            >
              <Input.TextArea
                rows={2}
                placeholder='{"newPassword":"3465"}'
                autoComplete="off"
              />
            </Form.Item>
          </>
        )}

        {(operation === 'Deactivate' || operation === 'Reactivate') && (
          <Paragraph type="secondary">
            La operacion se aplicara a los {selectedDeviceIds.length} dispositivos
            seleccionados que tengas permisos para modificar.
          </Paragraph>
        )}
      </Form>
    </Modal>
  );
}
